import { useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { addPositiveSymbol } from './symbol_utils'
import { useSmartInterval } from '../../generic/hooks/useSmartInterval'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { Alert, Box, Button, IconButton, Typography } from '@mui/material'
import { SmartSelect } from '../../generic/smartSelect'
import FeatureGroupSelect from './FeatureGroupsSelector'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import Document from '../document/Document'
import { TextSnippetOutlined } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import { useAuthorization } from '../../generic/hooks/useAuthorization'

const FeatureResourceAssignment = ({ resource, resourceType, resourceTypeName }) => {
  const [selectedFeatures, setSelectedFeatures] = useState([])
  const [features, setFeatures] = useState([])
  const [featureGroups, setFeatureGroups] = useState([])
  const [acceptDialog, setAcceptDialog] = useState(false)
  const { t_ } = useSmartTranslation()
  const [userFeatureGroups, setUserFeatureGroups] = useState(null)
  const [selectedRow, setSelectedRow] = useState({})
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [openFeatureGroupsDialog, setOpenFeatureGroupsDialog] = useState(false)
  const [editing, setEditing] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleteGroup, setDeleteGroup] = useState(null)
  const [deleteGroupDependencies, setDeleteGroupDependencies] = useState(null)
  const isUserAuthorized = useAuthorization()
  const qualitative = 'Qualitative'
  useEffect(() => {
    if (!resource) return

    const fetchFeatures = async () => {
      const features = await apiGet('features/' + resourceType + '/' + resource.id)
      setFeatures(features || [])
    }

    const fetchFeatureGroups = async () => {
      const featureGroups = await apiGet('featureGroups/' + resourceType + '/' + resource.id)
      setFeatureGroups(featureGroups || [])
    }
    fetchFeatures()
    fetchFeatureGroups()
  }, [resource, openFeatureGroupsDialog, resourceType])
  const checkUpperAndLowerValues = (row) => {
    if (row.upperTolerance == null || row.lowerTolerance == null || row.upperTolerance === '' || row.lowerTolerance === '') return ''
    const upperTolerance = parseFloat(row.upperTolerance)
    const lowerTolerance = parseFloat(row.lowerTolerance)
    const upperIsNumber = !isNaN(upperTolerance)
    const lowerIsNumber = !isNaN(lowerTolerance)

    if (!upperIsNumber || !lowerIsNumber) {
      return t_('Upper and lower tolerance must be numeric values')
    }

    if (upperTolerance <= lowerTolerance) {
      return t_('Upper tolerance must be higher than lower tolerance')
    }

    return ''
  }

  const qualitativeReadOnlyCondition = (row) => {
    if (row.feature?.featureType?.name === qualitative) {
      return true
    }
    return false
  }
  const columns = [
    {
      name: t_('Code'),
      field: 'code',
      readOnly: true,
      charLimit: 60
    },
    {
      name: t_('Name'),
      field: 'name',
      charLimit: 80,
      readOnly: true
    },
    {
      name: t_('Description'),
      field: 'feature.description',
      charLimit: 200,
      readOnly: true

    },
    {
      name: t_('Feature type'),
      field: 'feature.featureType.name',
      type: 'smartSelect',
      readOnly: true
    },
    {
      name: t_('Qualitative attribute'),
      field: 'qualitativeAttribute',
      readOnly: (rowData) => {
        return !qualitativeReadOnlyCondition(rowData)
      }
    },
    {
      name: t_('Frequency'),
      field: 'frequency',
      type: 'numeric'
    },
    {
      name: t_('Nominal'),
      field: 'value',
      readOnly: (rowData) => {
        return qualitativeReadOnlyCondition(rowData)
      },
      numeric: true

    },
    {
      name: t_('Upper tolerance'),
      field: 'upperTolerance',
      readOnly: (rowData) => {
        return qualitativeReadOnlyCondition(rowData)
      },
      condition: (row) => {
        return checkUpperAndLowerValues(row)
      }
    },
    {
      name: t_('Lower tolerance'),
      field: 'lowerTolerance',
      readOnly: (rowData) => {
        return qualitativeReadOnlyCondition(rowData)
      },
      condition: (row) => {
        return checkUpperAndLowerValues(row)
      }
    },
    {
      name: t_('Unit'),
      field: 'unit.code',
      type: 'select',
      edit: {
        source: async () => {
          return await apiGet('units')
        },
        id: 'id',
        field: 'code'
      },
      readOnly: (rowData) => {
        return qualitativeReadOnlyCondition(rowData)
      }
    }

  ]
  const actions = {
    edit: {
      fetchFunction: (row) => {
        return apiPut('featureResourceValue/', row)
      },
      disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_INDIVIDUAL_EDIT')
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('featureResourceValue/' + rows[0].id)
        } else {
          const featureResourceIds = rows.map(row => row.id)
          return apiDelete('featureResourceValues/deleteMulti/', featureResourceIds)
        }
      },
      disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_INDIVIDUAL_DELETE')
    },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: editing || !isUserAuthorized('DOCUMENTS_FEATURE_PART_REFERENCE'),
        onClick: (row) => {
          const data = { id: row.feature.id, code: row.feature.code, name: row.feature.name }
          setSelectedRow(data)
          setShowDocument(true)
        }
      }
    ]
  }

  const renderHeader = (group) => {
    return (
      <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
        <Typography variant='subtitle1'>{group.name}</Typography>
        <IconButton size='small' onClick={(event) => { event.stopPropagation(); setDeleteGroup(group); setOpenDeleteDialog(true) }} disabled={!isUserAuthorized('FEATURE_PART_REFERENCE_GROUP_DELETE')}>
          <DeleteIcon fontSize='small' color='primary' opacity={!isUserAuthorized('FEATURE_PART_REFERENCE_GROUP_DELETE') ? 0.5 : 1} />
        </IconButton>
      </Box>
    )
  }

  const handleGroupDelete = async () => {
    try {
      const response = await apiDelete('assignedFeatureGroup/' + resourceType + '/' + resource?.id + '/' + deleteGroup?.id)
      if (response && response.length > 0) {
        const featureNames = []
        response.forEach((item) => {
          userFeatureGroups?.grouped.forEach((group) => {
            const match = group.features.find((feature) => feature.id === item.resourceId)
            if (match) {
              featureNames.push(match.name)
            }
          })
        })
        const uniqueFeatureNames = [...new Set(featureNames)].join(', ')
        setDeleteGroupDependencies({
          groupId: deleteGroup.id,
          message: t_(
            'Cannot delete group because the following features have associated inspections:') + ' ' + uniqueFeatureNames
        })
      }
      setOpenDeleteDialog(false)
    } catch (error) {
      console.error(error)
      setDeleteGroupDependencies({
        groupId: deleteGroup.id,
        message: t_('An error occurred while deleting the group.')
      })
    }
  }
  const renderGroupedFeatures = (groupedFeatures, partReference, columns) => {
    return groupedFeatures.map((group) => {
      const actionGroup = {
        edit: {
          fetchFunction: (row) => {
            return apiPut('featureResourceValue/', row)
          },
          disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_INDIVIDUAL_EDIT')
        },
        delete: {
          fetchFunction: (rows) => {
            if (rows.length === 1) {
              return apiDelete('featureResourceValue/' + rows[0].id)
            } else {
              const featureResourceIds = rows.map(row => row.id)
              return apiDelete('featureResourceValues/deleteMulti/', featureResourceIds)
            }
          },
          disabled: !isUserAuthorized('FEATURE_PART_REFERENCE_INDIVIDUAL_DELETE')
        },
        custom: [
          {
            name: 'Documents',
            render: (row) => {
              return (
                <IconButton>
                  <TextSnippetOutlined />
                </IconButton>
              )
            },
            disabled: editing || !isUserAuthorized('DOCUMENTS_FEATURE_PART_REFERENCE'),
            onClick: (row) => {
              const data = { id: row.feature.id, code: row.feature.code, name: row.feature.name }
              setSelectedRow(data)
              setShowDocument(true)
            }
          }
        ]
      }
      return (
        <DefaultAccordion
          headerRender={renderHeader(group)}
          key={'feature-group-' + group.name}
          defaultExpanded={false}
          disabled={!isUserAuthorized('FEATURE_PART_REFERENCE_GROUP')}
        >
          {deleteGroupDependencies && deleteGroupDependencies.groupId === group.id && (
            <Alert
              severity='error'
              onClose={() => setDeleteGroupDependencies(null)}
              sx={{ width: '100%', mb: 2 }}
            >
              {deleteGroupDependencies.message}
            </Alert>
          )}

          <SmartMateriaUITable
            tableTuneUniqueKey='features'
            columns={columns}
            dataFetch={group.features}
            pageable={false}
            actions={actionGroup}
            multipleSelection
            safeDeleteShowProperty='name'
          />
        </DefaultAccordion>
      )
    })
  }
  const defaultToolbar = {
    renderSticky: {
      fetchFunction: () => {
        return (
          <Button color='primary' disabled={!isUserAuthorized('FEATURE_PART_REFERENCE_ASSIGNMENT')} variant='text' onClick={() => { setSelectedFeatures([]); setOpenFeatureGroupsDialog(true); setAcceptDialog(false) }}>{t_('Assignment')}</Button>
        )
      }
    }
  }
  const fetchData = async () => {
    const featureResourceValueList = await apiGet('featureResourceValue/' + resourceType + '/' + resource.id)
    const features = []
    const featureGroups = []
    featureResourceValueList.forEach((element) => {
      if (!element.feature) return

      const featureResourceValue = {
        ...element,
        id: element.id,
        value: element.value,
        unit: element.unit,
        upperTolerance: addPositiveSymbol(element.upperTolerance),
        lowerTolerance: addPositiveSymbol(element.lowerTolerance),
        qualitativeAttribute: element.qualitativeAttribute,
        feature: element.feature,
        name: element.feature?.name
      }

      if (featureResourceValue.feature?.featureGroup !== null) {
        let featureGroup = featureGroups.find((group) => group.id === featureResourceValue.feature?.featureGroup.id)
        if (featureGroup === undefined) {
          featureGroup = {
            id: featureResourceValue.feature?.featureGroup.id,
            name: featureResourceValue.feature?.featureGroup.name,
            features: []
          }
          featureGroups.push(featureGroup)
        }
        featureGroup.features.push(featureResourceValue)
      } else {
        features.push(featureResourceValue)
      }
    })
    return {
      grouped: featureGroups,
      ungrouped: features
    }
  }

  const setIntervalData = (data) => {
    setUserFeatureGroups(data)
  }
  useSmartInterval(fetchData, 1000, setIntervalData)
  const onChangeFeatures = (newNames) => {
    if (!Array.isArray(features)) return

    const newFeatures = features.filter((f) =>
      newNames.includes(f.name)
    )
    setSelectedFeatures(newFeatures)
  }

  const selectableOptionsFeatures = () => {
    return Array.isArray(features)
      ? features.map((opt) => opt.name)
      : []
  }

  const handleSaveFeatures = async () => {
    await apiPost('featureResourceValue/' + resourceType + '/' + resource.id, selectedFeatures)
    setOpenFeatureGroupsDialog(false)
  }
  return (
    <>
      <SmartDialog
        title={{ icon: <DeleteIcon color='primary' sx={{ fontSize: '1.5rem' }} />, render: <Typography variant='h5'>{t_('Group delete')}</Typography> }}
        message={t_('Are you sure you want to delete group: ') + deleteGroup?.name + '?'}
        cancel
        cancelCallback={() => {
          setOpenDeleteDialog(false)
        }}
        accept
        acceptCallback={() => {
          handleGroupDelete()
        }}
        setOpen={openDeleteDialog}
      />
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={selectedRow} resourceType='FEATURE' permissionResource='FEATURE_PART_REFERENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <SmartDialog
        setOpen={openFeatureGroupsDialog}
        title={{ icon: <PlaylistAddIcon color='primary' sx={{ fontSize: '6.5rem' }} />, render: <Typography variant='h4'>{t_('Features assignment')}</Typography> }}
        close={false}
        accept
        cancel
        cancelCallback={() => { setOpenFeatureGroupsDialog(false); setSelectedFeatures([]) }}
        acceptCallback={() => { handleSaveFeatures(selectedFeatures); setAcceptDialog(true) }}
        renderComponent={
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginTop: '20px', minWidth: '600px' }}>
            <div style={{ flex: 1 }}>
              <SmartSelect
                label={t_('Individuals')}
                value={selectedFeatures.map((f) => f.name)}
                multiple
                selectableOptions={selectableOptionsFeatures()}
                onChange={onChangeFeatures}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FeatureGroupSelect
                groups={featureGroups}
                acceptFromDialog={acceptDialog}
                partReferenceId={resource.id}
                resourceType={resourceType}
              />
            </div>
          </div>
          }
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='features'
        columns={columns}
        title={t_('Features of ' + resourceTypeName) + ': ' + resource.name}
        dataFetch={userFeatureGroups?.ungrouped || []}
        actions={actions}
        pageable={false}
        multipleSelection
        editingRow={(event) => setEditing(event)}
        toolbar={defaultToolbar}
        safeDeleteShowProperty='name'
      />
      {userFeatureGroups?.grouped !== null && userFeatureGroups?.grouped?.length > 0
        ? renderGroupedFeatures(userFeatureGroups.grouped, resource, columns)
        : null}
    </>
  )
}
export default FeatureResourceAssignment
